import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import { FirebaseAuthService, User } from '../services/firebaseAuth';
import serviceManager from '../services/serviceManager';

// User interface is now imported from firebaseAuth service

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<User>;
  register: (name: string, email: string, password: string) => Promise<User>;
  logout: () => void;
  updateUser: (userData: Partial<User>) => void;
  resetPassword: (email: string) => Promise<void>;
  signInWithGoogle: () => Promise<User>;
  signInWithFacebook: () => Promise<User>;
  signInWithTwitter: () => Promise<User>;
  signInWithInstagram: () => Promise<User>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Listen to Firebase auth state changes
    const unsubscribe = FirebaseAuthService.onAuthStateChanged(async (firebaseUser) => {
      if (firebaseUser) {
        // User is signed in
        const userData = await FirebaseAuthService.getUserData(firebaseUser.uid);
        if (userData) {
          setUser(userData);
          // Start services when user is authenticated
          serviceManager.startAllServices();
        }
      } else {
        // User is signed out
        setUser(null);
        // Stop services when user logs out
        serviceManager.stopAllServices();
      }
      setIsLoading(false);
    });

    // Handle OAuth redirect results
    FirebaseAuthService.handleRedirectResult().then((userData) => {
      if (userData) {
        setUser(userData);
        // Start services when user is authenticated via OAuth
        serviceManager.startAllServices();
      }
    });

    // Cleanup subscription on unmount
    return () => {
      unsubscribe();
      // Stop services on unmount
      serviceManager.stopAllServices();
    };
  }, []);

  // fetchUser function is no longer needed with Firebase

  const login = async (email: string, password: string) => {
    try {
      const userData = await FirebaseAuthService.login(email, password);
      setUser(userData);
      return userData;
    } catch (error: any) {
      throw error;
    }
  };

  const register = async (name: string, email: string, password: string) => {
    try {
      const userData = await FirebaseAuthService.register(email, password, name);
      setUser(userData);
      return userData;
    } catch (error: any) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      // Stop services before logout
      serviceManager.stopAllServices();
      
      await FirebaseAuthService.logout();
      setUser(null);
      // Redirect to login page
      window.location.href = '/login';
    } catch (error) {
      // Fallback: force reload to login page
      window.location.href = '/login';
    }
  };

  const updateUser = (userData: Partial<User>) => {
    if (user) {
      setUser({ ...user, ...userData });
    }
  };

  const resetPassword = async (email: string) => {
    try {
      await FirebaseAuthService.resetPassword(email);
    } catch (error: any) {
      throw error;
    }
  };

  // Social authentication methods
  const signInWithGoogle = async () => {
    try {
      const userData = await FirebaseAuthService.signInWithGoogle();
      setUser(userData);
      return userData;
    } catch (error: any) {
      throw error;
    }
  };

  const signInWithFacebook = async () => {
    try {
      const userData = await FirebaseAuthService.signInWithFacebook();
      setUser(userData);
      return userData;
    } catch (error: any) {
      throw error;
    }
  };

  const signInWithTwitter = async () => {
    try {
      const userData = await FirebaseAuthService.signInWithTwitter();
      setUser(userData);
      return userData;
    } catch (error: any) {
      throw error;
    }
  };

  const signInWithInstagram = async () => {
    try {
      const userData = await FirebaseAuthService.signInWithInstagram();
      setUser(userData);
      return userData;
    } catch (error: any) {
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    register,
    logout,
    updateUser,
    resetPassword,
    signInWithGoogle,
    signInWithFacebook,
    signInWithTwitter,
    signInWithInstagram,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
